import React, { useState, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Search, Loader2, RefreshCcw, FileText, FileSpreadsheet, Calendar } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useToast } from "@/components/ui/use-toast";
import jsPDF from 'jspdf';
import 'jspdf-autotable';

const ReportsPage = ({ fetchSales, operators = [], flags = [], paymentMethods = [] }) => {
  const { toast } = useToast();
  const [sales, setSales] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);

  // Filtros de porcentagem (taxa)
  const [filters, setFilters] = useState([
    { id: 1, condition: 'above', value: '', enabled: true },
    { id: 2, condition: 'below', value: '', enabled: true }
  ]);

  // Novos filtros: Operadora, Bandeira e Período
  const [selectedOperator, setSelectedOperator] = useState('all');
  const [selectedFlag, setSelectedFlag] = useState('all');
  const [selectedType, setSelectedType] = useState('all');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  // Carregar dados com filtros no servidor
  const loadReportData = async () => {
    if (!fetchSales) return;
    setLoading(true);
    setHasSearched(true);
    try {
      // Montar filtros para enviar ao servidor
      const serverFilters = {
        fetchAll: true
      };

      // Filtro de Operadora
      if (selectedOperator && selectedOperator !== 'all') {
        serverFilters.selectedOperators = new Set([selectedOperator]);
      }

      // Filtro de Bandeira
      if (selectedFlag && selectedFlag !== 'all') {
        serverFilters.selectedFlags = new Set([selectedFlag]);
      }

      // Filtro de Tipo (Forma de Pagamento)
      if (selectedType && selectedType !== 'all') {
        serverFilters.selectedPaymentMethods = new Set([selectedType]);
      }

      // Filtro de Período
      if (startDate) {
        serverFilters.dateRange = serverFilters.dateRange || {};
        serverFilters.dateRange.startDate = new Date(startDate + 'T00:00:00');
      }
      if (endDate) {
        serverFilters.dateRange = serverFilters.dateRange || {};
        serverFilters.dateRange.endDate = new Date(endDate + 'T23:59:59');
      }

      const { data } = await fetchSales({
        page: 1,
        pageSize: 100000,
        filters: serverFilters
      });
      setSales(data || []);
    } catch (error) {
      console.error("Erro ao carregar relatório", error);
      toast({ title: "Erro", description: "Falha ao carregar dados do relatório.", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  // Filtro de porcentagem (taxa) — aplicado no cliente sobre os dados já filtrados pelo servidor
  const filteredSales = useMemo(() => {
    if (!sales || !Array.isArray(sales)) {
      return [];
    }

    // Somente vendas Quitadas
    let result = sales.filter(sale => sale && sale.status === 'Quitado');

    // Aplicar filtros de porcentagem (taxa de desconto)
    const activeFilters = filters.filter(f => {
      if (!f.enabled || !f.value) return false;
      const numValue = parseFloat(f.value);
      return !isNaN(numValue) && isFinite(numValue);
    });

    if (activeFilters.length > 0) {
      result = result.filter(sale => {
        const valorCompra = parseFloat(sale.valorcompra || 0);
        const valorRecebido = parseFloat(sale.valorrecebido || 0);

        let taxPercentage = 0;
        if (valorCompra > 0 && valorRecebido > 0) {
          taxPercentage = ((valorCompra - valorRecebido) / valorCompra) * 100;
        }

        return activeFilters.every(filter => {
          const filterValue = parseFloat(filter.value);
          if (isNaN(filterValue) || !isFinite(filterValue)) return false;

          switch (filter.condition) {
            case 'above':
              return taxPercentage > filterValue;
            case 'below':
              return taxPercentage < filterValue;
            case 'equal':
              return Math.abs(taxPercentage - filterValue) < 0.01;
            default:
              return true;
          }
        });
      });
    }

    return result;
  }, [sales, filters]);

  // Totalizadores reativos dos cards (recalculam a cada mudança nos filtros)
  const summary = useMemo(() => {
    const totalVendas = filteredSales.reduce((sum, s) => sum + (parseFloat(s.valorcompra) || 0), 0);
    const totalRecebido = filteredSales.reduce((sum, s) => sum + (parseFloat(s.valorrecebido) || 0), 0);

    let mediaTaxa = 0;
    const vendasComTaxa = filteredSales.filter(s => {
      const vc = parseFloat(s.valorcompra || 0);
      const vr = parseFloat(s.valorrecebido || 0);
      return vc > 0 && vr > 0;
    });
    if (vendasComTaxa.length > 0) {
      const somaTaxas = vendasComTaxa.reduce((sum, s) => {
        const vc = parseFloat(s.valorcompra);
        const vr = parseFloat(s.valorrecebido);
        return sum + ((vc - vr) / vc) * 100;
      }, 0);
      mediaTaxa = somaTaxas / vendasComTaxa.length;
    }

    return { totalVendas, totalRecebido, mediaTaxa };
  }, [filteredSales]);

  const handleFilterChange = (id, key, value) => {
    setFilters(prev => prev.map(f => {
      if (f.id === id) {
        const updatedFilter = { ...f, [key]: value };

        if (key === 'value' && value !== '') {
          const numValue = parseFloat(value);
          if (isNaN(numValue) || !isFinite(numValue)) {
            return f;
          }
        }

        return updatedFilter;
      }
      return f;
    }));
  };

  const formatCurrency = (value) => {
    const numValue = parseFloat(value);
    if (isNaN(numValue)) {
      return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(0);
    }
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(numValue);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString.replace(/-/g, '/'));
    return date.toLocaleDateString('pt-BR');
  };

  const calcTaxPercent = (valorCompra, valorRecebido) => {
    const vc = parseFloat(valorCompra || 0);
    const vr = parseFloat(valorRecebido || 0);
    if (vc > 0 && vr > 0) {
      return (((vc - vr) / vc) * 100).toFixed(2).replace('.', ',') + '%';
    }
    return '0,00%';
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'Quitado': return 'status-quitado';
      default: return 'status-aberto';
    }
  };

  const handleExportCSV = () => {
    if (filteredSales.length === 0) {
      toast({ title: "Sem dados", description: "Não há dados para exportar.", variant: "destructive" });
      return;
    }

    const headers = ["Data", "Operadora", "Bandeira", "Tipo", "Parcelas", "Valor Compra", "Valor Recebido", "% Taxa", "Cod Autorizacao", "Status"];
    const csvRows = [headers.join(";")];

    filteredSales.forEach(sale => {
      const row = [
        formatDate(sale.data),
        sale.empresa,
        sale.bandeira,
        sale.tipotransacao,
        sale.parcelas,
        sale.valorcompra ? sale.valorcompra.toString().replace('.', ',') : "0,00",
        sale.valorrecebido ? sale.valorrecebido.toString().replace('.', ',') : "0,00",
        calcTaxPercent(sale.valorcompra, sale.valorrecebido),
        sale.codigoautorizacao,
        sale.status
      ];
      csvRows.push(row.join(";"));
    });

    const csvString = "\uFEFF" + csvRows.join("\n"); // UTF-8 BOM
    const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `relatorio_vendas_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    URL.revokeObjectURL(url);
    toast({ title: "Sucesso", description: "CSV exportado com sucesso." });
  };

  const handleExportPDF = () => {
    if (filteredSales.length === 0) {
      toast({ title: "Sem dados", description: "Não há dados para exportar.", variant: "destructive" });
      return;
    }

    const doc = new jsPDF();
    const dataGeracao = new Date().toLocaleDateString('pt-BR');
    const horaGeracao = new Date().toLocaleTimeString('pt-BR');

    doc.setFontSize(18);
    doc.setTextColor(40, 40, 40);
    doc.text("Relatório de Vendas", 14, 20);

    doc.setFontSize(10);
    doc.setTextColor(100, 100, 100);
    doc.text(`Gerado em: ${dataGeracao} às ${horaGeracao}`, 14, 28);

    // Filtros aplicados
    let filtersText = 'Filtros: ';
    if (selectedOperator && selectedOperator !== 'all') filtersText += `Operadora: ${selectedOperator} | `;
    if (selectedFlag && selectedFlag !== 'all') filtersText += `Bandeira: ${selectedFlag} | `;
    if (selectedType && selectedType !== 'all') filtersText += `Tipo: ${selectedType} | `;
    if (startDate) filtersText += `De: ${formatDate(startDate)} | `;
    if (endDate) filtersText += `Até: ${formatDate(endDate)} | `;
    if (filtersText === 'Filtros: ') filtersText += 'Nenhum';
    doc.text(filtersText, 14, 34);

    const head = [[
      "Data", "Operadora", "Bandeira", "Tipo", "Parc",
      "Valor", "Recebido", "% Taxa", "Status"
    ]];

    const body = filteredSales.map(sale => [
      formatDate(sale.data),
      sale.empresa,
      sale.bandeira,
      sale.tipotransacao,
      sale.parcelas,
      formatCurrency(sale.valorcompra),
      formatCurrency(sale.valorrecebido),
      calcTaxPercent(sale.valorcompra, sale.valorrecebido),
      sale.status
    ]);

    doc.autoTable({
      head: head,
      body: body,
      startY: 40,
      styles: { fontSize: 8 },
      headStyles: { fillColor: [22, 163, 74] },
    });

    doc.save(`relatorio_vendas_${new Date().toISOString().split('T')[0]}.pdf`);
    toast({ title: "Sucesso", description: "PDF gerado com sucesso." });
  };

  return (
    <div className="flex flex-col h-full text-white">
      <Card className="glass-effect border-white/20 mb-4">
        <CardHeader>
          <div className="flex justify-between items-center">
            <CardTitle className="text-white flex items-center gap-2">
              <Search className="w-5 h-5" /> Filtros do Relatório
            </CardTitle>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={handleExportCSV} disabled={loading || filteredSales.length === 0} className="text-white border-white/20 hover:bg-white/10">
                <FileSpreadsheet className="w-4 h-4 mr-2" />
                Excel
              </Button>
              <Button variant="outline" size="sm" onClick={handleExportPDF} disabled={loading || filteredSales.length === 0} className="text-white border-white/20 hover:bg-white/10">
                <FileText className="w-4 h-4 mr-2" />
                PDF
              </Button>
              <Button variant="outline" size="sm" onClick={loadReportData} disabled={loading} className="text-white border-white/20 hover:bg-white/10">
                {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <RefreshCcw className="w-4 h-4 mr-2" />}
                Atualizar Dados
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {/* Linha 1: Operadora, Bandeira, Tipo e Período */}
          <div className="grid grid-cols-1 md:grid-cols-5 gap-3 mb-4">
            <div>
              <label className="text-sm text-white/70">Operadora</label>
              <Select value={selectedOperator} onValueChange={setSelectedOperator}>
                <SelectTrigger className="bg-white/10 border-white/20 text-white">
                  <SelectValue placeholder="Todas" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas</SelectItem>
                  {operators.map((op) => (
                    <SelectItem key={op.id} value={op.name}>{op.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm text-white/70">Bandeira</label>
              <Select value={selectedFlag} onValueChange={setSelectedFlag}>
                <SelectTrigger className="bg-white/10 border-white/20 text-white">
                  <SelectValue placeholder="Todas" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas</SelectItem>
                  {flags.map((flag) => (
                    <SelectItem key={flag.id} value={flag.name}>{flag.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm text-white/70">Tipo</label>
              <Select value={selectedType} onValueChange={setSelectedType}>
                <SelectTrigger className="bg-white/10 border-white/20 text-white">
                  <SelectValue placeholder="Todos" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  {paymentMethods.map((pm) => (
                    <SelectItem key={pm.id} value={pm.name}>{pm.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm text-white/70 flex items-center gap-1"><Calendar className="w-3 h-3" /> Data Inicial</label>
              <Input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="bg-white/10 border-white/20 text-white"
              />
            </div>
            <div>
              <label className="text-sm text-white/70 flex items-center gap-1"><Calendar className="w-3 h-3" /> Data Final</label>
              <Input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="bg-white/10 border-white/20 text-white"
              />
            </div>
          </div>

          {/* Linha 2: Filtros de Porcentagem (Taxa) */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {filters.map((filter, index) => (
              <div key={filter.id} className="flex items-end gap-2">
                {index > 0 && <div className="self-center pb-2 font-bold">E</div>}
                <div className="flex-1">
                  <label className="text-sm text-white/70">Condição (% Taxa)</label>
                  <Select value={filter.condition} onValueChange={(value) => handleFilterChange(filter.id, 'condition', value)}>
                    <SelectTrigger className="bg-white/10 border-white/20 text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="above">Acima de</SelectItem>
                      <SelectItem value="below">Abaixo de</SelectItem>
                      <SelectItem value="equal">Igual a</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex-1">
                  <label className="text-sm text-white/70">Valor (%)</label>
                  <Input
                    type="number"
                    placeholder="Ex: 5"
                    value={filter.value}
                    onChange={(e) => handleFilterChange(filter.id, 'value', e.target.value)}
                    className="bg-white/10 border-white/20 text-white placeholder:text-white/50"
                  />
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <div className="flex-grow overflow-y-auto">
        <Card className="glass-effect border-white/20 h-full">
          <CardHeader>
            <div className="flex flex-col gap-4">
              <CardTitle className="text-white">Resultados da Busca ({filteredSales.length})</CardTitle>
              {filteredSales.length > 0 && (
                <div className="grid grid-cols-3 gap-3">
                  <div className="bg-white/5 border border-white/10 rounded-lg p-3 text-center">
                    <p className="text-xs text-white/60 mb-1">Vendas</p>
                    <p className="text-lg font-bold text-green-400">{formatCurrency(summary.totalVendas)}</p>
                  </div>
                  <div className="bg-white/5 border border-white/10 rounded-lg p-3 text-center">
                    <p className="text-xs text-white/60 mb-1">Recebidos</p>
                    <p className="text-lg font-bold text-blue-400">{formatCurrency(summary.totalRecebido)}</p>
                  </div>
                  <div className="bg-white/5 border border-white/10 rounded-lg p-3 text-center">
                    <p className="text-xs text-white/60 mb-1">% Taxa</p>
                    <p className="text-lg font-bold text-orange-400">{summary.mediaTaxa.toFixed(2).replace('.', ',')}%</p>
                  </div>
                </div>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-12"><Loader2 className="w-8 h-8 animate-spin mx-auto mb-2 text-white/70" /><p className="text-white/70">Carregando dados para análise...</p></div>
            ) : !hasSearched ? (
              <div className="text-center py-8">
                <p className="text-white/70">
                  Selecione os filtros desejados e clique em "Atualizar Dados" para buscar as vendas.
                </p>
              </div>
            ) : filteredSales.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-white/70">
                  Nenhuma venda encontrada com os critérios atuais.
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-white/20">
                      <th className="text-left p-3 text-white/80 font-medium">Data</th>
                      <th className="text-left p-3 text-white/80 font-medium">Operadora</th>
                      <th className="text-left p-3 text-white/80 font-medium">Bandeira</th>
                      <th className="text-left p-3 text-white/80 font-medium">Tipo</th>
                      <th className="text-left p-3 text-white/80 font-medium">Parcelas</th>
                      <th className="text-left p-3 text-white/80 font-medium">Valor</th>
                      <th className="text-left p-3 text-white/80 font-medium">Recebido</th>
                      <th className="text-left p-3 text-white/80 font-medium">% Taxa</th>
                      <th className="text-left p-3 text-white/80 font-medium">Cód. Autorização</th>
                      <th className="text-left p-3 text-white/80 font-medium">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredSales.map((sale) => (
                      <tr
                        key={sale.id}
                        className="border-b border-white/10 hover:bg-white/5 transition-colors"
                      >
                        <td className="p-3 text-white text-sm">{formatDate(sale.data)}</td>
                        <td className="p-3 text-white text-sm">{sale.empresa}</td>
                        <td className="p-3 text-white text-sm">{sale.bandeira}</td>
                        <td className="p-3 text-white text-sm capitalize">{sale.tipotransacao}</td>
                        <td className="p-3 text-white text-sm">{sale.parcelas}</td>
                        <td className="p-3 text-white text-sm">{formatCurrency(sale.valorcompra)}</td>
                        <td className="p-3 text-white text-sm">{formatCurrency(sale.valorrecebido)}</td>
                        <td className="p-3 text-white text-sm font-bold">
                          {calcTaxPercent(sale.valorcompra, sale.valorrecebido)}
                        </td>
                        <td className="p-3 text-white text-sm">{sale.codigoautorizacao}</td>
                        <td className="p-3"><Badge className={getStatusBadgeClass(sale.status)}>{sale.status}</Badge></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div >
  );
};

export default ReportsPage;
